import { useCallback, useRef } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import type { ActivityItemEdge, SearchPageNode } from '@atlassian/search-client';

import { useAppContext } from '../../../common/ui/app-context';
import { State } from '../../../common/utils/quick-find/state';
import { useLogException } from '../../error-boundary/utils';
import {
	transformQuickFindConfluenceRecentActivities,
	transformQuickFindConfluenceResults,
} from '../../utils/transform-fetch-results';
import type { QuickFindResult } from '../fetch-results/types';

export const useResultsShownCallbacks = () => {
	const logException = useLogException();
	const { onSearchResultsShown: onSearchResultsShownCallback } = useAppContext();
	const isPrequeryRecentActivitieHandled = useRef<boolean>(false);

	/*
	 * This is called only for POSTQUERY_RESULTS state
	 * */
	const onSearchResultsShown = useCallback(
		async (searchResults: QuickFindResult<SearchPageNode>[]): Promise<void> => {
			if (onSearchResultsShownCallback && searchResults.length > 0) {
				try {
					const confluenceSearchResults = transformQuickFindConfluenceResults(searchResults);

					if (confluenceSearchResults.length > 0) {
						onSearchResultsShownCallback(confluenceSearchResults);
					}
				} catch (e) {
					logException(e);
				}
			}
		},
		[onSearchResultsShownCallback, logException],
	);

	/*
	 * This is debounced to avoid triggering the callback if the list changes too quickly
	 *
	 * The recent activities are only available in PREQUERY_RESULTS, POSTQUERY_LOADING and POSTQUERY_RESULTS states.
	 * Since the "Recent Activities" list remains consistent in both POSTQUERY_LOADING and POSTQUERY_RESULTS states, we only report it during the POSTQUERY_LOADING state.
	 * */
	const [handleRecentActivitiesCallback] = useDebouncedCallback(
		async (state: string, recentActivities: QuickFindResult<ActivityItemEdge>[]) => {
			try {
				if (onSearchResultsShownCallback && recentActivities.length > 0) {
					/*
					 * The “Recent Activities” in PREQUERY_RESULTS state are reported just once, as they always show the same list after loading.
					 * */
					if (state === State.PREQUERY_RESULTS && !isPrequeryRecentActivitieHandled.current) {
						isPrequeryRecentActivitieHandled.current = true;

						const confluenceRecentActivities =
							transformQuickFindConfluenceRecentActivities(recentActivities);
						if (confluenceRecentActivities.length > 0) {
							onSearchResultsShownCallback(confluenceRecentActivities);
						}
					}

					if (state === State.POSTQUERY_LOADING) {
						const confluenceRecentActivities =
							transformQuickFindConfluenceRecentActivities(recentActivities);
						if (confluenceRecentActivities.length > 0) {
							onSearchResultsShownCallback(confluenceRecentActivities);
						}
					}
				}
			} catch (e) {
				logException(e);
			}
		},
		500,
	);

	// returning async callbacks to avoid blocking other operations
	return { onSearchResultsShown, handleRecentActivitiesCallback };
};
